import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Label,
  Row,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  AsyncAutocompleteCandidate,
  AsyncClientSelect,
} from "components/Common/AsyncSearchSelects";
import Loaders from "components/Common/Loaders";
import { useVerifyFadvCsvContext } from "libs/context/VerifyFadvCsvContext";
import {
  submitVerifyFadvCsvData,
  submitVerifyFadvCsvFilterData,
} from "model/CandidateModel";
import { pascalToCamel } from "helpers/GeneralHelper";
import {
  applyClientAsyncFilterOptionToFormState,
  buildCandidateAsyncFilterInitialOption,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";

const DEFAULT_COLUMNS = [
  { Header: "SN", accessor: "serialNumber" },
  { Header: "First name", accessor: "firstName" },
  { Header: "Last name", accessor: "lastName" },
  { Header: "Applicant email", accessor: "applicantEmail" },
  { Header: "Report status", accessor: "reportStatus" },
  { Header: "Report type", accessor: "reportType" },
  { Header: "Requestor", accessor: "requestor" },
  { Header: "Candidate", accessor: "candidateName" },
];

const mapColumnsFromMetadata = (metaColumns = []) => {
  if (!Array.isArray(metaColumns) || !metaColumns.length) return DEFAULT_COLUMNS;
  return metaColumns.map((col) => ({
    Header: col?.title || col?.field,
    accessor: pascalToCamel(col?.field) || col?.field,
    type: col?.type,
    asyncKind: col?.asyncKind,
  }));
};

const normalizeAccessorKey = (value = "") =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "");

const isCandidateColumn = (col = {}) => {
  const accessor = normalizeAccessorKey(col?.accessor);
  const asyncKind = String(col?.asyncKind ?? "").trim().toLowerCase();
  return (
    accessor === "candidateid" ||
    accessor === "candidatename" ||
    asyncKind === "candidate"
  );
};

const mapVerifyRow = (row = {}, index = 0) => {
  const rowId = row?.fadvOrderDataDetailsId ?? row?.id ?? row?._id ?? `row-${index}`;
  return {
    ...row,
    id: rowId,
    serialNumber: row?.serialNumber ?? index + 1,
    candidateId: row?.candidateId ?? "",
    candidateIdLabel: row?.candidateName ?? row?.candidateIdLabel ?? "",
    candidateName: row?.candidateName ?? "",
    _rowKey: String(rowId),
  };
};

/** Same client id as AsyncClientSelect / candidate-autocomplete (`clientId` state). */
const toSubmitClientId = (selectedClientId = "") => {
  const trimmed = String(selectedClientId ?? "").trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  return trimmed;
};

const buildSubmitRow = (row = {}, submitClientId) => ({
  serialNumber: Number(row?.serialNumber) || 0,
  fadvOrderDataDetailsId: String(row?.fadvOrderDataDetailsId ?? ""),
  firstName: String(row?.firstName ?? ""),
  lastName: String(row?.lastName ?? ""),
  reportStatus: String(row?.reportStatus ?? ""),
  reportType: String(row?.reportType ?? ""),
  requestor: String(row?.requestor ?? ""),
  applicantEmail: String(row?.applicantEmail ?? ""),
  clientId: submitClientId,
  candidateId: String(row?.candidateId ?? ""),
  candidateName: String(row?.candidateName ?? row?.candidateIdLabel ?? ""),
});

const StatusBadge = ({ value }) => {
  const text = String(value ?? "").trim();
  if (!text) return <span className="text-muted">—</span>;
  const lower = text.toLowerCase();
  const color =
    lower === "yes" || lower === "eligible" || lower.includes("pass")
      ? "success"
      : lower === "no" || lower.includes("fail")
        ? "secondary"
        : lower.includes("progress")
          ? "info"
          : "primary";
  return (
    <Badge color={color} className="candidate-csv-preview-badge">
      {text}
    </Badge>
  );
};

const renderCellValue = (col, row) => {
  const accessor = col?.accessor;
  const value = row?.[accessor];

  if (value === null || value === undefined || String(value).trim() === "") {
    return <span className="text-muted">—</span>;
  }

  if (normalizeAccessorKey(accessor) === "reportstatus") {
    return <StatusBadge value={value} />;
  }

  return String(value);
};

const VerifyFadvCsv = () => {
  const {
    fetchVerifyFadvCsvContextData,
    verifyFadvCsvContextData,
    loading: metadataLoading,
  } = useVerifyFadvCsvContext() || {};

  const [clientId, setClientId] = useState("");
  const [clientIdLabel, setClientIdLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tableMetaData, setTableMetadata] = useState(DEFAULT_COLUMNS);
  const [verifyRows, setVerifyRows] = useState([]);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    fetchVerifyFadvCsvContextData?.();
  }, []);

  useEffect(() => {
    const valueReModified = mapColumnsFromMetadata(verifyFadvCsvContextData?.table?.columns);
    setTableMetadata(valueReModified);
  }, [verifyFadvCsvContextData]);

  const loadVerifyList = useCallback(async (selectedClientId) => {
    const id = String(selectedClientId ?? "").trim();
    if (!id) {
      setVerifyRows([]);
      return;
    }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setVerifyRows([]);

    try {
      const response = await submitVerifyFadvCsvFilterData({ clientId: id });
      if (fetchSeqRef.current !== seq) return;

      const responseData = response?.data || {};
      const listData = responseData?.records || [];

      if (response?.success) {
        const rows = (Array.isArray(listData) ? listData : []).map(mapVerifyRow);
        setVerifyRows(rows);
        // if (!rows.length) {
        //   toast.info(response?.message || "No records found for this client.");
        // }
      } else {
        setVerifyRows([]);
        toast.error(response?.message || "Unable to load FADV verify list.");
      }
    } catch (err) {
      if (fetchSeqRef.current !== seq) return;
      setVerifyRows([]);
      toast.error(err?.message || "Unable to load FADV verify list.");
    } finally {
      if (fetchSeqRef.current === seq) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = String(clientId ?? "").trim();
    if (!id) {
      setVerifyRows([]);
      return undefined;
    }
    loadVerifyList(id);
    return () => {
      fetchSeqRef.current += 1;
    };
  }, [clientId, loadVerifyList]);

  const handleClientChange = (option) => {
    const nextForm = applyClientAsyncFilterOptionToFormState(
      { clientId, [getClientAsyncFilterLabelFieldKey()]: clientIdLabel },
      option
    );
    setClientId(String(nextForm?.clientId ?? "").trim());
    setClientIdLabel(String(nextForm?.[getClientAsyncFilterLabelFieldKey()] ?? "").trim());
  };

  const handleCandidateChange = (rowKey, option) => {
    setVerifyRows((prev) =>
      prev.map((row) =>
        row._rowKey === rowKey
          ? {
              ...row,
              candidateId: String(option?.value ?? "").trim(),
              candidateIdLabel: String(option?.label ?? "").trim(),
              candidateName: String(option?.label ?? "").trim(),
            }
          : row
      )
    );
  };

  const handleCancelClient = () => {
    fetchSeqRef.current += 1;
    setClientId("");
    setClientIdLabel("");
    setVerifyRows([]);
    setLoading(false);
  };

  const handleSubmit = async () => {
    const resolvedClientId = String(clientId ?? "").trim();
    if (!resolvedClientId) {
      toast.error("Client is required.");
      return;
    }
    if (!verifyRows.length) {
      toast.error("No records to submit.");
      return;
    }

    const submitClientId = toSubmitClientId(resolvedClientId);
    if (submitClientId === null || submitClientId === "") {
      toast.error("Client is required.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        clientId: submitClientId,
        data: verifyRows.map((row) => buildSubmitRow(row, submitClientId)),
      };
      const response = await submitVerifyFadvCsvData(payload);
      if (response?.success) {
        toast.success(response?.message || "FADV CSV submitted successfully.");
        setClientId("");
        setClientIdLabel("");
        setVerifyRows([]);
      } else {
        toast.error(response?.message || "Unable to submit FADV CSV.");
      }
    } catch (err) {
      toast.error(err?.message || "Unable to submit FADV CSV.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredData = useMemo(() => verifyRows, [verifyRows]);
  const showTable = Boolean(clientId) && !loading && filteredData.length > 0;

  return (
    <div className="page-content verify-fadv-csv-page">
      <Container fluid>
        <div className="verify-fadv-csv-header mb-4">
          <div className="verify-fadv-csv-header-icon">
            <i className="mdi mdi-file-check-outline" aria-hidden />
          </div>
          <div>
            <h4 className="fw-bold mb-1">Verify FADV CSV</h4>
            <p className="text-muted mb-0">
              Select a client to load FADV CSV records and match each row to a candidate.
            </p>
          </div>
        </div>

        <Card className="verify-fadv-csv-panel border-0 shadow-sm">
          <CardBody className="p-4">
            <Row className="g-3 verify-fadv-csv-form-row">
              <Col xs="12" md="6" lg="4">
                <div className="verify-fadv-csv-field">
                  <Label className="verify-fadv-csv-label fw-semibold">
                    Client <span className="text-danger">*</span>
                  </Label>
                  <div className="verify-fadv-csv-control">
                    <AsyncClientSelect
                      key={`verify-fadv-csv-client-${clientId}`}
                      pageName="VerifyFadvCsv"
                      value={clientId}
                      initialOption={buildClientAsyncFilterInitialOption(clientId, clientIdLabel)}
                      onChange={handleClientChange}
                      placeholder="Select Client"
                      isDisabled={loading || submitting}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {metadataLoading || (loading && clientId) ? (
          <Card className="candidate-csv-preview-card border-0 shadow-sm mt-4">
            <CardBody className="p-5 text-center">
              <Loaders />
            </CardBody>
          </Card>
        ) : null}

        {!metadataLoading && !loading && showTable ? (
          <Card className="candidate-csv-preview-card border-0 shadow-sm mt-4">
            <CardBody className="p-4">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <h5 className="fw-bold mb-1">FADV verify records</h5>
                  <p className="text-muted mb-0 small">
                    {filteredData.length} record{filteredData.length === 1 ? "" : "s"}
                    {clientId ? (
                      <>
                        {" "}
                        · Client: <span className="fw-semibold">{clientIdLabel || clientId}</span>
                      </>
                    ) : null}
                  </p>
                </div>
               
              </div>

              <div className="table-responsive candidate-csv-preview-table-wrap">
                <Table className="candidate-csv-preview-table align-middle mb-0" hover>
                  <thead>
                    <tr>
                      {tableMetaData.map((col) => (
                        <th key={col.accessor}>{col.Header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((row) => (
                      <tr key={row._rowKey}>
                        {tableMetaData.map((col) => (
                          <td key={`${row._rowKey}-${col.accessor}`}>
                            {isCandidateColumn(col) ? (
                              <AsyncAutocompleteCandidate
                                clientId={clientId}
                                value={row.candidateId}
                                initialOption={buildCandidateAsyncFilterInitialOption(
                                  row.candidateId,
                                  row.candidateIdLabel
                                )}
                                onChange={(option) =>
                                  handleCandidateChange(row._rowKey, option)
                                }
                                placeholder="Select candidate"
                                isDisabled={submitting}
                              />
                            ) : (
                              renderCellValue(col, row)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
               
              </div>
              <div className="text-end mt-3 d-flex justify-content-end gap-2">
                <Button
                  color="light"
                  className="border"
                  type="button"
                  onClick={handleCancelClient}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button color="primary" type="button" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Confirming..." : "Confirm"}
                </Button>
              </div>
            </CardBody>
          </Card>
        ) : null}

        {!metadataLoading && !loading && clientId && !filteredData.length ? (
          <Card className="candidate-csv-preview-card border-0 shadow-sm mt-4">
            <CardBody className="p-4 text-center text-muted">No data found</CardBody>
          </Card>
        ) : null}
      </Container>
    </div>
  );
};

VerifyFadvCsv.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(VerifyFadvCsv);
